import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save, User, Mail, Lock, UserCheck, Building } from 'lucide-react';
import { usuariosApi, UpdateUsuarioRequest } from '../../apis/usuariosApi';
import { toast } from 'react-toastify';

const EditarUsuario: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<UpdateUsuarioRequest>();

  const { data: usuario, isLoading: usuarioLoading } = useQuery({
    queryKey: ['usuario', id],
    queryFn: () => usuariosApi.getById(Number(id)),
    enabled: !!id
  });

  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: usuariosApi.getRoles
  });

  const { data: plataformas, isLoading: plataformasLoading } = useQuery({
    queryKey: ['plataformas'],
    queryFn: usuariosApi.getPlataformas
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateUsuarioRequest) => usuariosApi.update(Number(id), data),
    onSuccess: () => {
      toast.success('Usuario actualizado exitosamente');
      navigate('/usuarios');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al actualizar usuario');
      setIsSubmitting(false);
    }
  });

  useEffect(() => {
    if (usuario) {
      reset({
        nombre: usuario.nombre,
        apellido_paterno: usuario.apellido_paterno,
        apellido_materno: usuario.apellido_materno || '',
        correo: usuario.correo,
        rol_id: usuario.rol_id,
        plataforma_id: usuario.plataforma_id
      });
    }
  }, [usuario, reset]);

  const onSubmit = (data: any) => {
    setIsSubmitting(true);
    updateMutation.mutate({
      ...data,
      rol_id: Number(data.rol_id),
      plataforma_id: Number(data.plataforma_id),
    });
  };

  const handleBack = () => {
    navigate('/usuarios');
  };

  if (usuarioLoading || rolesLoading || plataformasLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-300">Cargando datos...</p>
        </div>
      </div>
    );
  }

  if (!usuario) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400">Usuario no encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          .editar-usuario-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
            color: white;
            padding: 2rem;
          }

          .content-wrapper {
            max-width: 800px;
            margin: 0 auto;
          }

          .page-header {
            text-align: left;
            margin-bottom: 3rem;
          }

          .page-title {
            font-size: 2.5rem;
            font-weight: 700;
            background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 0.5rem;
          }

          .page-subtitle {
            color: rgba(255, 255, 255, 0.7);
            font-size: 1.1rem;
          }

          .back-button {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 12px;
            color: white;
            text-decoration: none;
            font-weight: 500;
            transition: all 0.3s ease;
            margin-bottom: 2rem;
          }

          .back-button:hover {
            background: rgba(255, 255, 255, 0.2);
            transform: translateY(-2px);
          }

          .form-container {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
          }

          .form-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
            margin-bottom: 2rem;
          }

          .form-group {
            display: flex;
            flex-direction: column;
          }

          .form-group.full-width {
            grid-column: 1 / -1;
          }

          .form-label {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.9);
            font-size: 0.875rem;
          }

          .form-input, .form-select {
            padding: 12px 16px;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 12px;
            color: white;
            font-size: 1rem;
            transition: all 0.3s ease;
          }

          .form-input:focus, .form-select:focus {
            outline: none;
            background: rgba(255, 255, 255, 0.15);
            border-color: #007AFF;
            box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
          }

          .form-input::placeholder {
            color: rgba(255, 255, 255, 0.5);
          }

          .form-select option {
            background: #1a1a1a;
            color: white;
          }

          .form-error {
            color: #FF3B30;
            font-size: 0.75rem;
            margin-top: 4px;
          }

          .password-note {
            color: rgba(255, 255, 255, 0.6);
            font-size: 0.75rem;
            margin-top: 4px;
          }

          .form-actions {
            display: flex;
            gap: 1rem;
            justify-content: flex-end;
            padding-top: 2rem;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
          }

          .btn {
            padding: 12px 24px;
            border-radius: 12px;
            font-weight: 500;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: all 0.3s ease;
            border: none;
            cursor: pointer;
            font-size: 1rem;
          }

          .btn-primary {
            background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
            color: white;
          }

          .btn-primary:hover:not(:disabled) {
            background: linear-gradient(135deg, #0056CC 0%, #003D99 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 122, 255, 0.3);
          }

          .btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-secondary {
            background: rgba(255, 255, 255, 0.1);
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .btn-secondary:hover {
            background: rgba(255, 255, 255, 0.2);
          }

          @media (max-width: 768px) {
            .form-grid {
              grid-template-columns: 1fr;
            }
            
            .form-actions {
              flex-direction: column;
            }
          }
        `
      }} />

      <div className="editar-usuario-container">
        <div className="content-wrapper">
          <button onClick={handleBack} className="back-button">
            <ArrowLeft size={16} />
            Volver a usuarios
          </button>

          <div className="page-header">
            <h1 className="page-title">Editar Usuario</h1>
            <p className="page-subtitle">Modifica la información del usuario {usuario.nombre} {usuario.apellido_paterno}</p>
          </div>

          <div className="form-container">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <User size={16} />
                    Nombre *
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Ingresa el nombre"
                    {...register('nombre', { 
                      required: 'El nombre es requerido',
                      minLength: { value: 2, message: 'Mínimo 2 caracteres' }
                    })}
                  />
                  {errors.nombre && <span className="form-error">{errors.nombre.message}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <User size={16} />
                    Apellido Paterno *
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Ingresa el apellido paterno"
                    {...register('apellido_paterno', { 
                      required: 'El apellido paterno es requerido',
                      minLength: { value: 2, message: 'Mínimo 2 caracteres' }
                    })}
                  />
                  {errors.apellido_paterno && <span className="form-error">{errors.apellido_paterno.message}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <User size={16} />
                    Apellido Materno
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Ingresa el apellido materno (opcional)"
                    {...register('apellido_materno')}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <Mail size={16} />
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="usuario@ejemplo.com"
                    {...register('correo', { 
                      required: 'El correo es requerido',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Correo electrónico inválido'
                      }
                    })}
                  />
                  {errors.correo && <span className="form-error">{errors.correo.message}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <Lock size={16} />
                    Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    placeholder="Dejar vacío para mantener la actual"
                    {...register('contrasena', { 
                      minLength: { value: 6, message: 'Mínimo 6 caracteres' }
                    })}
                  />
                  <span className="password-note">Dejar vacío para mantener la contraseña actual</span>
                  {errors.contrasena && <span className="form-error">{errors.contrasena.message}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <UserCheck size={16} />
                    Rol *
                  </label>
                  <select
                    className="form-select"
                    {...register('rol_id', { 
                      required: 'El rol es requerido'
                    })}
                  >
                    <option value="">Selecciona un rol</option>
                    {roles?.map((rol) => (
                      <option key={rol.id} value={String(rol.id)}>
                        {rol.nombre}
                      </option>
                    ))}
                  </select>
                  {errors.rol_id && <span className="form-error">{errors.rol_id.message}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <Building size={16} />
                    Plataforma *
                  </label>
                  <select
                    className="form-select"
                    {...register('plataforma_id', { 
                      required: 'La plataforma es requerida'
                    })}
                  >
                    <option value="">Selecciona una plataforma</option>
                    {plataformas?.map((plataforma) => (
                      <option key={plataforma.id} value={String(plataforma.id)}>
                        {plataforma.nombre}
                      </option>
                    ))}
                  </select>
                  {errors.plataforma_id && <span className="form-error">{errors.plataforma_id.message}</span>}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleBack} className="btn btn-secondary">
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  <Save size={16} />
                  {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditarUsuario;
